import React, { useState } from 'react';

import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';

import CrudForm from '../../../../Form/CrudForm';
import Table from '../../../../Table';
import TransactionsTableModal from './TransactionsTableModal';

import useUserRole from '../../../../../hooks/useUserRole';
import BankStatementCollectionService from '../../../../../services/BankStatementCollectionService';
import { useUiStore } from '../../../../../store';
import { sumMoney } from '../../../../Table/helpers/calculateTotals';

export default function BankStatementCollection({
  bankStatementCollection,
  refetch,
  loanFile,
}) {
  const [isTransactionsTableModalVisible, setIsTransactionsTableModalVisible] =
    useState(false);
  const [currentBankStatementId, setCurrentBankStatementId] = useState(null);

  const { isSuperAdmin } = useUserRole();

  const notifySuccess = useUiStore((state) => state.notifySuccess);
  const notifyApiError = useUiStore((state) => state.notifyApiError);

  const borrowerOptions =
    loanFile?.borrowers?.map((borrower) => ({
      label: `${borrower.firstName} ${borrower.lastName}`,
      value: borrower.id,
    })) || [];

  const fields = [
    {
      label: 'Belongs to',
      placeholder: 'Select a borrower',
      name: 'borrowerId',
      type: 'select',
      validation: Yup.string(),
      fullWidth: true,
      options: borrowerOptions,
    },
    {
      label: 'Account Holder',
      placeholder: 'Type first name',
      name: 'name',
      type: 'text',
      validation: Yup.string().required('This field is required'),
    },
    {
      label: 'Pay Loan out of the Account',
      name: 'payLoanWith',
      type: 'select',
      validation: Yup.string().required('This field is required'),
      options: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
      placeholder: 'Select an option',
    },
    {
      label: 'Type',
      name: 'type',
      type: 'select',
      options: [
        {
          label: 'Checking',
          value: 'Checking',
        },
        {
          label: 'Savings',
          value: 'Savings',
        },
      ],
      placeholder: 'Select an option',
    },
    {
      label: 'Personal or Business',
      name: 'personalOrBusiness',
      type: 'select',
      options: [
        {
          label: 'Personal',
          value: 'Personal',
        },
        {
          label: 'Business',
          value: 'Business',
        },
      ],
      placeholder: 'Select an option',
    },
    {
      label: 'Financial Institution',
      name: 'bankName',
      type: 'text',
    },
    {
      label: 'Average Balance',
      name: 'averageBalance',
      type: 'currency',
      readOnly: true,
    },
    {
      label: 'Account Number',
      name: 'accountNumber',
      type: 'text',
    },
    {
      label: 'Average Deposit',
      name: 'averageDeposit',
      type: 'currency',
      readOnly: true,
    },
    {
      label: 'Routing Number',
      name: 'routingNumber',
      type: 'text',
    },
    {
      label: 'Available Cash Override',
      name: 'availableCashOverride',
      type: 'currency',
    },
    {
      label: 'Months Provided',
      name: 'monthsProvided',
      type: 'text',
      readOnly: true,
    },
    {
      label: 'Available Cash',
      name: 'availableCash',
      type: 'currency',
      readOnly: true,
    },
    {
      label: 'Expense Ratio',
      name: 'expenseRatio',
      type: 'text',
    },
    {
      label: 'Recognized Income',
      name: 'recognizedIncome',
      type: 'currency',
      readOnly: true,
    },
  ];

  const cols = [
    {
      title: 'Reg Date',
      key: 'beginningDate',
      type: 'date',
    },
    {
      title: 'Deposits',
      key: 'depositsTotal',
      type: 'currency',
      onClick: (value, row) => {
        setCurrentBankStatementId(row.id);
        setIsTransactionsTableModalVisible(true);
      },
      valueTooltip: (value, row) => {
        if (!row.transactions.length) {
          return null;
        }

        const totalDeposits = sumMoney(
          row.transactions
            .map((transaction) => transaction.depositAmount)
            .filter(Boolean)
        );

        if (totalDeposits === value) {
          return null;
        }

        return 'The bank statement deposit total does not match the sum of the deposits';
      },
    },
    {
      title: 'Exclusions',
      key: 'exclusionsSum',
      type: 'currency',
      onClick: (value, row) => {
        setCurrentBankStatementId(row.id);
        setIsTransactionsTableModalVisible(true);
      },
    },
    {
      title: 'Net Deposits',
      key: 'netDeposits',
      type: 'currency',
    },
    {
      title: 'Ending Balance',
      key: 'endingBalance',
      type: 'currency',
    },
  ];

  const { mutate: updateBankStatementCollection, isPending } = useMutation({
    mutationFn: (formValues) =>
      BankStatementCollectionService.updateBankStatementCollection({
        formValues,
        id: bankStatementCollection.id,
      }),
    onSuccess: () => {
      notifySuccess('Bank Statement Collection updated successfully');
      refetch();
    },
    onError: (error) => {
      console.log(error);
      notifyApiError(error);
    },
  });

  const currentBankStatement = bankStatementCollection?.bankStatements?.find(
    (bs) => bs.id === currentBankStatementId
  );

  return (
    <div className="h-full">
      <CrudForm
        fields={fields}
        data={bankStatementCollection}
        disabled={!isSuperAdmin}
        submitButtonLabel="Update"
        updateHandler={updateBankStatementCollection}
        isPending={isPending}
      />

      <div className="mx-auto max-w-screen-xl pt-6">
        <Table
          cols={cols}
          rows={bankStatementCollection?.bankStatements}
          countName="Bank Statements"
        />
      </div>
      <TransactionsTableModal
        isVisible={isTransactionsTableModalVisible}
        hideModal={() => setIsTransactionsTableModalVisible(false)}
        transactions={currentBankStatement?.transactions}
        refetch={refetch}
        bankStatementId={currentBankStatementId}
      />
    </div>
  );
}
